// src/pages/PlayTime.jsx
import { Fragment, useMemo, useRef, useState } from 'react';
import { WebLayout } from 'components/Layout';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Dialog, Transition } from '@headlessui/react';
import {
  Search,
  Sliders,
  Instagram,
  MessageCircle,
  Send,
  MapPin,
  Calendar as CalendarIcon,
  Tag,
  Users,
  Camera,
  Play,
  X,
  Copy,
  Check,
} from 'react-feather';
import { Link } from 'react-router-dom';

const ORDER_LINKS = {
  instagram: 'https://instagram.com/gambitgamesug',
  whatsappNumber: '256700000000', // no plus sign
  telegram: 'https://t.me/gambitgamesug',
};

const GAME_FILTERS = [
  { id: 'all', label: 'All games' },
  { id: 'chess', label: 'Chess' },
  { id: 'ludo', label: 'Ludo' },
  { id: 'omweso', label: 'Omweso/Mancala' },
  { id: 'carrom', label: 'Carrom' },
  { id: 'dominoes', label: 'Dominoes' },
  { id: 'jenga', label: 'Jenga' },
  { id: 'backgammon', label: 'Backgammon' },
];

const VIBES = [
  { id: 'all', label: 'All vibes' },
  { id: 'family', label: 'Family night' },
  { id: 'date', label: 'Date night' },
  { id: 'cafe', label: 'Café' },
  { id: 'outdoor', label: 'Outdoor' },
  { id: 'kids', label: 'Kids' },
  { id: 'corporate', label: 'Corporate' },
];

const HASHTAGS =
  '#GambitGamesUG #MadeInUganda #Kampala #BoardGameNight #WoodenBoardGames #FamilyGameNight #Omweso #ChessLife';

// Mock gallery items (replace with your real images/videos)
const GALLERY = [
  {
    id: 'g1',
    type: 'image',
    src: '/assets/playtime/chess_cafe_1.jpg',
    title: 'Chess at the café',
    game: 'chess',
    vibe: 'cafe',
    location: 'Kisaasi, Kampala',
    people: 2,
    date: '2025-07-21',
    tags: ['2-player', 'mahogany', 'coffee'],
    credit: '@kisaasicafe',
    aspect: 'aspect-[4/3]',
  },
  {
    id: 'g2',
    type: 'image',
    src: '/assets/playtime/ludo_family_1.jpg',
    title: 'Family Ludo laughs',
    game: 'ludo',
    vibe: 'family',
    location: 'Najjera, Kampala',
    people: 4,
    date: '2025-07-14',
    tags: ['family', 'color-inlay'],
    credit: '@nakato_family',
    aspect: 'aspect-square',
  },
  {
    id: 'g3',
    type: 'video',
    src: '/assets/playtime/jenga_party_1.mp4',
    poster: '/assets/playtime/jenga_party_1_poster.jpg',
    title: 'Jenga tower moment',
    game: 'jenga',
    vibe: 'outdoor',
    location: 'Bukoto, Kampala',
    people: 5,
    date: '2025-07-30',
    tags: ['party', 'kids'],
    credit: '@bukoto_blockparty',
    aspect: 'aspect-[9/16]',
  },
  {
    id: 'g4',
    type: 'image',
    src: '/assets/playtime/omweso_trad_1.jpg',
    title: 'Omweso tradition',
    game: 'omweso',
    vibe: 'family',
    location: 'Ntinda, Kampala',
    people: 2,
    date: '2025-06-28',
    tags: ['cultural', 'mugavu'],
    credit: '@ntinda_culture',
    aspect: 'aspect-[3/2]',
  },
  {
    id: 'g5',
    type: 'image',
    src: '/assets/playtime/carrom_friends_1.jpg',
    title: 'Carrom with friends',
    game: 'carrom',
    vibe: 'cafe',
    location: 'Kololo, Kampala',
    people: 4,
    date: '2025-07-10',
    tags: ['4-player', 'smooth-surface'],
    credit: '@kololo_boardnights',
    aspect: 'aspect-[4/3]',
  },
  {
    id: 'g6',
    type: 'image',
    src: '/assets/playtime/dominoes_patio_1.jpg',
    title: 'Dominoes on the patio',
    game: 'dominoes',
    vibe: 'outdoor',
    location: 'Naguru, Kampala',
    people: 2,
    date: '2025-07-18',
    tags: ['classic', 'tiles'],
    credit: '@naguru_evenings',
    aspect: 'aspect-[4/3]',
  },
  {
    id: 'g7',
    type: 'image',
    src: '/assets/playtime/date_chess_1.jpg',
    title: 'Date-night checkmate',
    game: 'chess',
    vibe: 'date',
    location: 'Acacia, Kampala',
    people: 2,
    date: '2025-07-05',
    tags: ['2-player', 'romance'],
    credit: '@acacia_lounge',
    aspect: 'aspect-[3/4]',
  },
  {
    id: 'g8',
    type: 'image',
    src: '/assets/playtime/kids_tictactoe_1.jpg',
    title: 'Kids Tic-Tac-Toe',
    game: 'jenga', // keep vibe as kids; game filter not strictly needed here—use jenga as misc
    vibe: 'kids',
    location: 'Najjera, Kampala',
    people: 2,
    date: '2025-06-21',
    tags: ['kids', 'travel'],
    credit: '@gambitgamesug',
    aspect: 'aspect-square',
  },
  {
    id: 'g9',
    type: 'image',
    src: '/assets/playtime/backgammon_cafe_1.jpg',
    title: 'Backgammon & brew',
    game: 'backgammon',
    vibe: 'cafe',
    location: 'Bugolobi, Kampala',
    people: 2,
    date: '2025-07-24',
    tags: ['2-player', 'inlay'],
    credit: '@bugolobi_brews',
    aspect: 'aspect-[4/3]',
  },
  {
    id: 'g10',
    type: 'image',
    src: '/assets/playtime/corporate_gift_1.jpg',
    title: 'Corporate game break',
    game: 'dominoes',
    vibe: 'corporate',
    location: 'Kampala CBD',
    people: 4,
    date: '2025-07-12',
    tags: ['corporate', 'bundle'],
    credit: '@company_ug',
    aspect: 'aspect-[3/2]',
  },
  {
    id: 'g11',
    type: 'image',
    src: '/assets/playtime/ludo_balcony_1.jpg',
    title: 'Balcony Ludo',
    game: 'ludo',
    vibe: 'outdoor',
    location: 'Kira, Kampala',
    people: 4,
    date: '2025-07-01',
    tags: ['family', 'balcony'],
    credit: '@kira_balcony',
    aspect: 'aspect-[4/3]',
  },
  {
    id: 'g12',
    type: 'image',
    src: '/assets/playtime/omweso_sunset_1.jpg',
    title: 'Omweso at sunset',
    game: 'omweso',
    vibe: 'outdoor',
    location: 'Muyenga, Kampala',
    people: 2,
    date: '2025-07-26',
    tags: ['cultural', 'sunset'],
    credit: '@muyenga_views',
    aspect: 'aspect-[3/4]',
  },
];

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-UG', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

export function PlayTime() {
  // Parallax hero
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.85]);

  // Filters
  const [query, setQuery] = useState('');
  const [game, setGame] = useState('all');
  const [vibe, setVibe] = useState('all');

  // Modal
  const [selected, setSelected] = useState(null);
  const [copied, setCopied] = useState(false);

  const filtered = useMemo(() => {
    return GALLERY.filter((it) => (game === 'all' ? true : it.game === game))
      .filter((it) => (vibe === 'all' ? true : it.vibe === vibe))
      .filter((it) =>
        `${it.title} ${it.tags?.join(' ')} ${it.location} ${it.credit}`
          .toLowerCase()
          .includes(query.toLowerCase())
      );
  }, [game, vibe, query]);

  const copyCaption = async (it) => {
    const caption = `${it.title} • ${it.game.toUpperCase()} • ${it.location}\n${HASHTAGS}`;
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // swallow
    }
  };

  return (
    <WebLayout>
      {/* HERO */}
      <section ref={heroRef} className="relative isolate overflow-hidden bg-gray-950">
        <motion.div style={{ y, opacity }} className="pointer-events-none absolute inset-0">
          <img
            src="/assets/playtime_hero.jpg"
            alt=""
            className="h-full w-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-950/60 via-gray-950/60 to-gray-950" />
        </motion.div>

        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#12A2BF]/30 bg-[#12A2BF]/10 px-3 py-1 text-xs font-medium text-[#12A2BF]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#12A2BF]" />
              Made in Uganda • Free delivery within Kampala.
            </div>
            <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Playtime — Uganda Plays Gambit Games
            </h1>
            <p className="mt-3 max-w-xl text-sm text-gray-300">
              Real moments from homes, cafés, balconies, and events. Tag{' '}
              <a
                href={ORDER_LINKS.instagram}
                target="_blank"
                rel="noreferrer"
                className="text-[#12A2BF] underline"
              >
                @gambitgamesug
              </a>{' '}
              to get featured.
            </p>
          </div>

          {/* Filters */}
          <div className="mt-8 grid gap-3 md:grid-cols-[1fr_auto_auto]">
            {/* Search */}
            <div className="flex items-center overflow-hidden rounded-xl border border-gray-800 bg-gray-900">
              <div className="px-3 text-gray-400">
                <Search size={18} />
              </div>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search title, tags, location, credit…"
                className="w-full bg-transparent px-2 py-3 text-sm text-gray-100 placeholder-gray-500 focus:outline-none"
              />
            </div>

            {/* Game filter */}
            <div className="flex items-center gap-2 overflow-hidden rounded-xl border border-gray-800 bg-gray-900 px-3">
              <Camera size={16} className="text-gray-400" />
              <select
                value={game}
                onChange={(e) => setGame(e.target.value)}
                className="w-full bg-transparent py-2 text-sm text-gray-100 focus:outline-none"
                aria-label="Filter by game"
              >
                {GAME_FILTERS.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Vibe filter */}
            <div className="flex items-center gap-2 overflow-hidden rounded-xl border border-gray-800 bg-gray-900 px-3">
              <Sliders size={16} className="text-gray-400" />
              <select
                value={vibe}
                onChange={(e) => setVibe(e.target.value)}
                className="w-full bg-transparent py-2 text-sm text-gray-100 focus:outline-none"
                aria-label="Filter by vibe"
              >
                {VIBES.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Submission CTA */}
          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-gray-300">
            <span>Want to be featured?</span>
            <a
              href={ORDER_LINKS.instagram}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-full bg-pink-500/90 px-2.5 py-1 text-white"
            >
              <Instagram size={14} />
              Tag @gambitgamesug
            </a>
            <span>or DM us your photo/video.</span>
          </div>
        </div>
      </section>

      {/* GALLERY GRID */}
      <section className="bg-gray-950">
        <div className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
          {filtered.length === 0 ? (
            <div className="rounded-lg border border-gray-800 bg-gray-900 p-8 text-center">
              <p className="text-gray-300">No moments found. Try another search or filter.</p>
              <div className="mt-3 text-sm text-gray-400">
                Tip: Search “café”, “family”, “omweso”, “balcony”, etc.
              </div>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((it, i) => (
                <motion.button
                  key={it.id}
                  onClick={() => setSelected(it)}
                  className="group overflow-hidden rounded-2xl border border-gray-800 bg-gray-900 text-left"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03, duration: 0.3 }}
                >
                  <div className={`relative ${it.aspect} overflow-hidden bg-gray-800`}>
                    {it.type === 'video' ? (
                      <video
                        src={it.src}
                        poster={it.poster}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        muted
                        playsInline
                        preload="metadata"
                      />
                    ) : (
                      <img
                        src={it.src}
                        alt={it.title}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    )}
                    {it.type === 'video' && (
                      <div className="pointer-events-none absolute inset-0 grid place-items-center">
                        <div className="rounded-full bg-black/50 p-2">
                          <Play className="h-5 w-5 text-white" />
                        </div>
                      </div>
                    )}
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                      <div className="line-clamp-1 text-sm font-medium text-white">{it.title}</div>
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-300">
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={12} />
                          {it.location}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Users size={12} /> {it.people}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* VIEWER DIALOG */}
      <Transition show={!!selected} as={Fragment}>
        <Dialog as="div" className="relative z-[80]" onClose={() => setSelected(null)}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/70" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="transition transform duration-300 ease-out"
                enterFrom="translate-y-6 opacity-0"
                enterTo="translate-y-0 opacity-100"
                leave="transition transform duration-200 ease-in"
                leaveFrom="translate-y-0 opacity-100"
                leaveTo="translate-y-6 opacity-0"
              >
                <Dialog.Panel className="w-full max-w-4xl overflow-hidden rounded-2xl border border-gray-800 bg-gray-900 shadow-2xl">
                  {selected && (
                    <div className="grid gap-0 lg:grid-cols-2">
                      <div className="relative bg-black">
                        {selected.type === 'video' ? (
                          <video
                            src={selected.src}
                            poster={selected.poster}
                            className="h-full w-full"
                            controls
                            playsInline
                          />
                        ) : (
                          <img
                            src={selected.src}
                            alt={selected.title}
                            className="h-full w-full object-contain"
                          />
                        )}
                        <button
                          onClick={() => setSelected(null)}
                          className="absolute right-2 top-2 rounded-md bg-black/60 p-1 text-white hover:bg-black/80"
                          aria-label="Close"
                        >
                          <X size={18} />
                        </button>
                      </div>

                      <div className="flex flex-col p-5">
                        <Dialog.Title className="text-lg font-semibold text-white">
                          {selected.title}
                        </Dialog.Title>
                        <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-gray-400">
                          <span className="inline-flex items-center gap-1">
                            <MapPin size={14} />
                            {selected.location}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <CalendarIcon size={14} />
                            {formatDate(selected.date)}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Users size={14} />
                            {selected.people} players
                          </span>
                        </div>

                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="inline-flex items-center gap-1 rounded-full border border-gray-800 bg-gray-950 px-2 py-0.5 text-[11px] text-gray-300">
                            <Tag size={12} />
                            {selected.game}
                          </span>
                          <span className="inline-flex items-center gap-1 rounded-full border border-gray-800 bg-gray-950 px-2 py-0.5 text-[11px] text-gray-300">
                            <Tag size={12} />
                            {selected.vibe}
                          </span>
                          {selected.tags?.map((t) => (
                            <span
                              key={t}
                              className="inline-flex items-center gap-1 rounded-full border border-gray-800 bg-gray-950 px-2 py-0.5 text-[11px] text-gray-300"
                            >
                              <Tag size={12} />
                              {t}
                            </span>
                          ))}
                        </div>

                        <div className="mt-4 rounded-lg border border-gray-800 bg-gray-950 p-3 text-xs text-gray-400">
                          Credit: <span className="text-gray-300">{selected.credit}</span>
                        </div>

                        {/* CTAs */}
                        <div className="mt-4 grid gap-2 sm:grid-cols-3">
                          <a
                            href={ORDER_LINKS.instagram}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-pink-500/90 px-3 py-2 text-sm font-semibold text-white hover:brightness-110"
                          >
                            <Instagram size={16} />
                            View @gambitgamesug
                          </a>
                          <a
                            href={`https://wa.me/${
                              ORDER_LINKS.whatsappNumber
                            }?text=${encodeURIComponent(
                              `Hi! I’d like to be featured on Playtime. I’ll share my photo/video here.`
                            )}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#12A2BF] px-3 py-2 text-sm font-semibold text-black hover:brightness-110"
                          >
                            <MessageCircle size={16} />
                            Submit via WhatsApp
                          </a>
                          <a
                            href={ORDER_LINKS.telegram}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-sky-500 px-3 py-2 text-sm font-semibold text-white hover:brightness-110"
                          >
                            <Send size={16} />
                            Submit via Telegram
                          </a>
                        </div>

                        <button
                          onClick={() => copyCaption(selected)}
                          className="mt-3 inline-flex items-center justify-center gap-2 rounded-md border border-gray-700 px-3 py-2 text-sm text-gray-200 hover:bg-gray-800"
                        >
                          {copied ? <Check size={16} /> : <Copy size={16} />}
                          {copied ? 'Caption copied' : 'Copy caption with hashtags'}
                        </button>

                        <p className="mt-3 text-xs text-gray-500">
                          By sending media you confirm you have rights to share it and you allow
                          repost on our platforms.
                        </p>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* FINAL CTA */}
      <section className="bg-gray-950">
        <div className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-gray-800 bg-gray-900 p-6 text-center">
            <h2 className="text-lg font-semibold text-white">Share Your Playtime</h2>
            <p className="mx-auto mt-2 max-w-2xl text-sm text-gray-300">
              Tag{' '}
              <a
                href={ORDER_LINKS.instagram}
                target="_blank"
                rel="noreferrer"
                className="text-[#12A2BF] underline"
              >
                @gambitgamesug
              </a>{' '}
              on Instagram or message us your clip. We love to feature Kampala’s game nights!
            </p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <a
                href={ORDER_LINKS.instagram}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-md bg-pink-500/90 px-4 py-2 text-sm font-semibold text-white hover:brightness-110"
              >
                <Instagram size={16} />
                Tag us on Instagram
              </a>
              <a
                href={`https://wa.me/${ORDER_LINKS.whatsappNumber}?text=${encodeURIComponent(
                  'Hi! I have a photo/video for Playtime.'
                )}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-md bg-[#12A2BF] px-4 py-2 text-sm font-semibold text-black hover:brightness-110"
              >
                <MessageCircle size={16} />
                WhatsApp us
              </a>
              <a
                href={ORDER_LINKS.telegram}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-md bg-sky-500 px-4 py-2 text-sm font-semibold text-white hover:brightness-110"
              >
                <Send size={16} />
                Telegram
              </a>
              <Link
                to="/custom"
                className="inline-flex items-center gap-2 rounded-md border border-gray-700 px-4 py-2 text-sm text-gray-200 hover:bg-gray-800"
              >
                Order a custom game →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </WebLayout>
  );
}
